import { FunctionComponent, memo, useEffect } from "react";
import { FormProvider, SubmitHandler, useForm } from "react-hook-form";
import { IQuestionType } from "./IQuestionType";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import TextInputFieldC from "../../Components/TextInput/TextInputFieldC";
import { useAppDispatch, useAppSelector } from "../../Redux/Store/Hooks";
import { addQuestionType } from "../../Redux/Slices/QuestionType/AddQuestionTypeSlice";
import { EditQuestionType } from "../../Redux/Slices/QuestionType/EditQuestionTypeSlice";
import Select from "../../Components/Select/AutoComplete";
import { AutocompleteOption } from "../../Components/Select/ISelectProps";
import TextAreaInputField from "../../Components/TextAreaInput/TextAreaInputField";
import { TextField } from "@mui/material";
import NumberInputField from "../../Components/NumberInput/NumberInputField";
import { ListSubjectList } from "../../Redux/Slices/QuestionType/ListSubjectListSlice";

interface QuestionTypeFormProps {
  formRef: React.RefObject<HTMLFormElement>;
  initialValues: IQuestionType;
  setInitialValues: React.Dispatch<React.SetStateAction<IQuestionType>>;
}

const QuestionTypeForm: FunctionComponent<QuestionTypeFormProps> = (props) => {
  const dispatch = useAppDispatch();

  // Define allowed image MIME types
  const allowedMimeTypes = ["image/jpeg", "image/png", "image/gif"];

  // Define the maximum file size (e.g., 5MB)
  const MAX_FILE_SIZE = 1 * 1024 * 1024; // 5 MB in bytes

  const userSchema = z.object({
    id: z.number().optional(),
    name: z
      .string({ message: "Please enter question type name" })
      .min(1, { message: "Please enter question type name" })
      .max(50, { message: "question type name cannot exceed 50 characters" }),
    sequence: z
      .number({ message: "Please enter sequence" })
      .min(1, { message: "Sequence must be at least 1" })
      .max(100, { message: "Sequence cannot exceed 100" }),

    subject_id: z.number().nullable().optional(),
    image: z
      .union([
        z
          .instanceof(File)
          .refine((file) => !!file, {
            message: "File is required.",
          })
          .refine((file) => allowedMimeTypes.includes(file.type), {
            message:
              "Invalid file type. Only JPEG, PNG, and GIF files are allowed.",
          })
          .refine((file) => file.size <= MAX_FILE_SIZE, {
            message: "File size should not exceed 1MB.",
          }),
        z.string().url(),
        z.literal(null),
        z.undefined(),
      ])
      .optional(),
    description: z
      .string()
      .min(1, { message: "Please enter description " })
      .max(250, { message: "description cannot exceed 250 characters" }), // <-- allow null and optional
    Status: z.boolean({
      message: "Please select the status",
    }),
  });

  const methods = useForm<IQuestionType>({
    defaultValues: {
      ...props.initialValues,
      Status: props.initialValues.id ? props.initialValues.Status : true,
    },

    resolver: zodResolver(userSchema),
    mode: "onChange",
  });

  const onSubmit: SubmitHandler<IQuestionType> = (val) => {
    if (val.id) {
      dispatch(EditQuestionType(val));
    } else {
      dispatch(addQuestionType(val));
    }
  };

  useEffect(() => {
    return () => {
      props.setInitialValues({});
    };
  }, [dispatch, props]);

  // Status list
  const QuestionTypeSatus = [
    {
      label: "Active",
      value: true,
    },

    {
      label: "Inactive",
      value: false,
    },
  ];

  // const { data: listCoursess } = useAppSelector(
  //   (state) => state.ListCoursesSlice
  // );
  useEffect(() => {
    dispatch(ListSubjectList());
  }, []);

  const { data: listCoursess } = useAppSelector(
    (state) => state.ListSubjectListSliceReducer //List_Question_SubTypeSliceReducer
  );
  //

  const ListYearLevel = Array.isArray(listCoursess?.data)
    ? listCoursess?.data?.map((listQuestions) => ({
        label: listQuestions?.full_name!,
        value: listQuestions.id,
      }))
    : [];

  const imageFile: any = methods.watch("image");

  return (
    <FormProvider {...methods}>
      <form
        ref={props.formRef}
        onSubmit={methods.handleSubmit(onSubmit)}
        className="form_double_input"
      >
        <TextInputFieldC name="name" label="Question Type" type="text" />
        <TextField
          fullWidth
          type="file"
          // size="small"
          label="Question Image"
          InputLabelProps={{ shrink: true }} // Correct way to shrink label
          onChange={(e: any) => {
            const file = e.target.files ? e.target.files[0] : null;

            methods.setValue("image", file, {
              shouldValidate: true,
            });
          }}
          error={!!methods.formState.errors.image}
          helperText={
            !!methods.formState.errors.image
              ? String(
                  methods.formState.errors.image.message ===
                    "Input not instance of File"
                    ? "Please upload question image"
                    : methods.formState.errors.image.message
                )
              : ""
          }
        />

        <TextAreaInputField
          name="description"
          label="Description"
          type="text"
        />
        <Select
          name="subject_id"
          label="Select  the subject"
          options={ListYearLevel ?? []}
          value={
            ListYearLevel?.find(
              (list) => list.value === methods.getValues("subject_id")
            ) ?? null
          }
          onChange={(_, value) => {
            const val = value as AutocompleteOption;
            methods.setValue("subject_id", val?.value, {
              shouldValidate: true,
            });
          }}
        />

        <NumberInputField name="sequence" label="Sequence" type="number" />

        <Select
          name="Status"
          label="Status"
          options={QuestionTypeSatus}
          value={
            QuestionTypeSatus?.find(
              (role) => role.value === methods.getValues("Status")
            ) ?? null
          }
          onChange={(_, value) => {
            const val = value as AutocompleteOption;
            methods.setValue("Status", val?.value, { shouldValidate: true });
          }}
        />
        {imageFile && (
          <div>
            <span style={{ color: "#009DFF" }}>Preview for Question Image</span>
            <img
              src={
                typeof imageFile === "string"
                  ? imageFile
                  : URL.createObjectURL(imageFile)
              }
              alt="Uploaded preview"
              style={{ maxWidth: "100%", height: "auto" }}
            />
            {/* <img
              src={imageFile}
              alt="Uploaded preview"
              style={{ maxWidth: "100%", height: "auto" }}
            /> */}
          </div>
        )}
      </form>
    </FormProvider>
  );
};

export default memo(QuestionTypeForm);
